import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getStaticFileUrl } from "../utils/config";

// easing 함수 (easeInOutQuad)
const easeInOutQuad = (t: number) =>
  t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

// requestAnimationFrame을 이용한 스크롤 애니메이션 함수
const animateScrollTo = (
  element: HTMLElement,
  target: number,
  duration: number,
  callback?: () => void
) => {
  const start = element.scrollLeft;
  const change = target - start;
  let startTime: number | null = null;

  const animate = (timestamp: number) => {
    if (startTime === null) startTime = timestamp;
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    element.scrollLeft = start + change * easeInOutQuad(progress);
    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      if (callback) callback();
    }
  };

  requestAnimationFrame(animate);
};

// 카드 데이터 타입 정의
interface CardData {
  imgSrc: string;
  logoSrc: string;
  title: string;
  description: string;
}

// 빈 기본 카드 배열
const defaultCards: CardData[] = [];

const Portfolio: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // 드래그 관련 상태
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [initialScrollLeft, setInitialScrollLeft] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  // 최신 isAutoScrolling 값을 참조 (stale closure 문제 해결)
  const isAutoScrollingRef = useRef(isAutoScrolling);
  useEffect(() => {
    isAutoScrollingRef.current = isAutoScrolling;
  }, [isAutoScrolling]);

  // 자동 스크롤 시 현재 카드 인덱스를 관리 (인덱스 기반 target 계산)
  const cardIndexRef = useRef(0);

  // 홈 포트폴리오 편집 데이터 상태
  const [headingTitle, setHeadingTitle] = useState('PORTFOLIO');
  const [subLine1, setSubLine1] = useState('대표적인 성공사례를');
  const [subLine2, setSubLine2] = useState('소개합니다.');
  const [cards, setCards] = useState(defaultCards);

  useEffect(() => {
    // API에서 포트폴리오 데이터 불러오기
    const loadPortfolioData = async () => {
      try {
        const { portfolioApi } = await import('../services/api');
        const data = await portfolioApi.getHomePortfolioData();
        
        console.log('API 응답 데이터:', data);
        if (data && data.headingTitle) {
          setHeadingTitle(data.headingTitle);
          setSubLine1(data.subLine1);
          setSubLine2(data.subLine2);
          if (Array.isArray(data.cards) && data.cards.length > 0) {
            console.log('HomePortfolio 카드 데이터:', data.cards);
            setCards(data.cards);
          }
        }
      } catch (error) {
        console.error('포트폴리오 데이터 로드 실패:', error);
      }
    };

    loadPortfolioData();
  }, []);

  // 마우스 드래그 이벤트 핸들러
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setIsAutoScrolling(false); // 드래그 시 자동 스크롤 중단
    setStartX(event.pageX - (scrollRef.current?.offsetLeft || 0));
    setInitialScrollLeft(scrollRef.current?.scrollLeft || 0);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollRef.current) return;
    event.preventDefault();
    const x = event.pageX - (scrollRef.current.offsetLeft || 0);
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = initialScrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsAutoScrolling(true); // 드래그 종료 후 자동 스크롤 재개
  };

  // 자동 스크롤 로직
  useEffect(() => {
    if (!isAutoScrolling || !scrollRef.current) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const animateNextCard = () => {
      if (!scrollRef.current) return;

      // 컨테이너의 gap 값 읽기
      const containerStyle = window.getComputedStyle(scrollRef.current);
      const gap = parseFloat(containerStyle.gap) || 0;

      // 첫 번째 카드의 전체 너비 (offsetWidth + gap)
      const firstCard = scrollRef.current.firstElementChild as HTMLElement;
      if (!firstCard) return;
      const cardTotalWidth = firstCard.offsetWidth + gap;

      const { scrollLeft, clientWidth, scrollWidth } = scrollRef.current;
      const maxScrollLeft = scrollWidth - clientWidth;

      // 현재 인덱스 (왼쪽 정렬 기준)
      const currentIndex = Math.floor(scrollLeft / cardTotalWidth);
      // 데스크탑은 카드 2개씩, 모바일은 1개씩 이동
      const scrollByCount = isMobile ? 1 : 2;
      let nextIndex = currentIndex + scrollByCount;
      let targetCandidate = nextIndex * cardTotalWidth;

      // 만약 targetCandidate가 최대 스크롤 영역보다 크다면 마지막 카드가 완전히 보이도록 targetCandidate 조정
      if (targetCandidate > maxScrollLeft) {
        targetCandidate = maxScrollLeft;
      }
      // 만약 이미 최대 영역에 가까이 있다면 wrap-around하여 처음으로 이동
      if (scrollLeft >= maxScrollLeft - 1) {
        targetCandidate = 0;
        nextIndex = 0;
      }

      cardIndexRef.current = nextIndex;
      animateScrollTo(scrollRef.current, targetCandidate, 500, () => {
        timeoutId = setTimeout(() => {
          if (isAutoScrollingRef.current) {
            animateNextCard();
          }
        }, 2000);
      });
    };

    animateNextCard();

    return () => clearTimeout(timeoutId);
  }, [isAutoScrolling, isMobile]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "30% 70%" },
        gridTemplateRows: { xs: "auto auto", md: "100%" },
        width: "100%",
        minHeight: { xs: "80vh", md: "100vh" },
        overflow: "hidden",
        background: "linear-gradient(to bottom, #efefef 60%, #ffffff 40%)",
      }}
    >
      {/* 텍스트 섹션 */}
      <Box
        sx={{
          textAlign: "right",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: { xs: "1rem", md: "12rem 2rem" },
        }}
      >
        <Box>
          <Typography
            variant={isMobile ? "h6" : "h3"}
            sx={{ fontWeight: "bold", color: "#333", marginBottom: "8px" }}
          >
            {headingTitle}
          </Typography>
          <Typography
            sx={{
              fontSize: isMobile ? "12px" : "18px",
              color: "#666",
              lineHeight: "1.4",
            }}
          >
            {subLine1}
            <br />
            {subLine2}
          </Typography>
          <Box
            sx={{
              marginTop: "0.8rem",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Box
              onClick={() => {
                navigate("/portfolio");
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }, 100);
              }}
              sx={{
                textDecoration: "none",
                padding: theme.spacing(1, 3),
                color: "#d82828",
                border: "0.1rem solid #d82828",
                borderRadius: "1.875rem",
                fontSize: isMobile ? "0.7rem" : "1rem",
                fontWeight: "400",
                textAlign: "center",
                transition: "background-color 0.3s, border-color 0.3s",
                ":hover": {
                  backgroundColor: "#d82828",
                  borderColor: "#d82828",
                  color: "#fff",
                },
                width: "auto",
                display: "inline-block",
                maxWidth: "fit-content",
                cursor: "pointer",
              }}
            >
              자세히 알아보기
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 카드 섹션 */}
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          overflowX: "hidden", // 스크롤바 감춤
          padding: { xs: "1rem", md: "8rem 2rem" },
          gap: "1rem",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          alignItems: "center",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {cards.map((card, index) => (
          <Box
            key={index}
            sx={{
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "auto",
              width: { xs: "80%", sm: "40%" },
              scrollSnapAlign: "start",
              flexShrink: 0,
            }}
          >
            {/* 이미지 섹션 - 16:9 비율 */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                paddingTop: "56.25%", // 16:9 비율 (9/16 = 56.25%)
                overflow: "hidden",
              }}
            >
              <img
                src={getStaticFileUrl(card.imgSrc)}
                alt={card.title}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
            <Box
              sx={{
                padding: "1rem",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: isMobile ? "1rem" : "1.5rem",
                  fontWeight: "400",
                  color: "#333",
                }}
              >
                {card.description}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                <Button
                  onClick={() => {
                    navigate("/portfolio");
                    setTimeout(() => {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }, 100);
                  }}
                  sx={{
                    fontSize: isMobile ? "0.875rem" : "1rem",
                    color: "#00548e",
                    textTransform: "none",
                    maxWidth: "100%",
                  }}
                >
                  더보기
                </Button>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Portfolio;
